import React from 'react';
import styled from 'styled-components';

import {ActionButton, Input} from './styles';

/**
 * Редактор характеристик автомобиля по группам.
 *
 * Формат хранения: массив групп `[{title, rows: [{label, value}]}]`.
 * Набор строк внутри группы произвольный — оператор заполняет то, что есть в паспорте.
 * Стартовые группы и подсказки повторяют карточку автомобиля в приложении.
 */

const GENERAL_HINTS = [
  'Длина, мм',
  'Ширина, мм',
  'Высота, мм',
  'Колёсная база, мм',
  'Клиренс, мм',
  'Объём багажника, л',
  'Снаряжённая масса, кг',
  'Полная масса, кг',
];

const PERFORMANCE_HINTS = [
  'Модель или тип двигателя',
  'Пиковая мощность, кВт',
  'Пиковая мощность, л.с.',
  'Максимальный крутящий момент, Н·м',
  'Тип охлаждения',
  'Максимальная скорость, км/ч',
];

export const defaultSpecGroups = () => ([
  {title: 'Общие параметры', rows: GENERAL_HINTS.map(label => ({label, value: ''}))},
  {title: 'Эксплуатационные характеристики', rows: PERFORMANCE_HINTS.map(label => ({label, value: ''}))},
]);

function SpecsEditor({value, onChange}) {
  const groups = Array.isArray(value) && value.length ? value : [];

  const update = next => onChange(next);

  const changeGroupTitle = (index, title) => {
    const next = groups.map((group, position) => (
      position === index ? {...group, title} : group
    ));
    update(next);
  };

  const changeRow = (groupIndex, rowIndex, field, fieldValue) => {
    const next = groups.map((group, position) => {
      if (position !== groupIndex) { return group; }
      const rows = group.rows.map((row, rowPosition) => (
        rowPosition === rowIndex ? {...row, [field]: fieldValue} : row
      ));
      return {...group, rows};
    });
    update(next);
  };

  const addRow = groupIndex => {
    const next = groups.map((group, position) => (
      position === groupIndex
        ? {...group, rows: [...group.rows, {label: '', value: ''}]}
        : group
    ));
    update(next);
  };

  const removeRow = (groupIndex, rowIndex) => {
    const next = groups.map((group, position) => (
      position === groupIndex
        ? {...group, rows: group.rows.filter((row, rowPosition) => rowPosition !== rowIndex)}
        : group
    ));
    update(next);
  };

  const addGroup = () => update([...groups, {title: 'Новая группа', rows: [{label: '', value: ''}]}]);

  const removeGroup = groupIndex => update(groups.filter((group, position) => position !== groupIndex));

  return (
    <Wrapper>
      <Header>
        <strong>Характеристики по группам</strong>
        <Hint>
          Пустые строки не сохраняются. Порядок групп и строк повторяется в карточке автомобиля
          в приложении.
        </Hint>
      </Header>

      {groups.length === 0 && (
        <Hint>Характеристики не заданы.</Hint>
      )}

      {groups.map((group, groupIndex) => (
        <Group key={`group-${groupIndex}`}>
          <GroupHeader>
            <Input
              value={group.title}
              onChange={event => changeGroupTitle(groupIndex, event.target.value)}
              placeholder="Название группы"
            />
            <ActionButton type="button" onClick={() => removeGroup(groupIndex)}>
              Удалить группу
            </ActionButton>
          </GroupHeader>

          {group.rows.map((row, rowIndex) => (
            <Row key={`row-${groupIndex}-${rowIndex}`}>
              <Input
                value={row.label}
                onChange={event => changeRow(groupIndex, rowIndex, 'label', event.target.value)}
                placeholder="Название характеристики"
              />
              <Input
                value={row.value}
                onChange={event => changeRow(groupIndex, rowIndex, 'value', event.target.value)}
                placeholder="Значение"
              />
              <RemoveButton type="button" onClick={() => removeRow(groupIndex, rowIndex)} aria-label="Удалить строку">
                ×
              </RemoveButton>
            </Row>
          ))}

          <ActionButton type="button" onClick={() => addRow(groupIndex)}>
            Добавить строку
          </ActionButton>
        </Group>
      ))}

      <ActionButton type="button" onClick={addGroup}>Добавить группу</ActionButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e1e5ea;
`;

const Header = styled.div`
  margin-bottom: 12px;
`;

const Hint = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #8b949f;
`;

const Group = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  border: 1px solid #e1e5ea;
  border-radius: 8px;
`;

const GroupHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  margin-bottom: 10px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 32px;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
`;

const RemoveButton = styled.button`
  height: 32px;
  border: 1px solid #e1e5ea;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
`;

export default SpecsEditor;
